import { AppConfig } from '@/types/config.types';
import { debug } from '@/config/constants';

class ConfigService {
  private config: AppConfig | null = null;
  private loadPromise: Promise<AppConfig> | null = null;

  async loadConfig(): Promise<AppConfig> {
    // Return cached config if already loaded
    if (this.config) {
      return this.config;
    }

    // Return existing promise if load is in progress
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start new load
    this.loadPromise = this.fetchConfig();
    this.config = await this.loadPromise;
    this.loadPromise = null;

    return this.config;
  }

  private async fetchConfig(): Promise<AppConfig> {
    try {
      const response = await fetch('/config.js');
      
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.statusText}`);
      }

      const config = await response.json();

      // Validate required fields
      this.validateConfig(config);

      debug.log('✓ Configuration loaded successfully');
      return config as AppConfig;

    } catch (error) {
      console.error('✗ Failed to load configuration:', error);
      throw new Error('Failed to load application configuration. Please refresh the page.');
    }
  }

  private validateConfig(config: any): void {
    const requiredFields = [
      'API_ENDPOINT',
      'WS_API_ENDPOINT',
      'REGION',
      'COGNITO_USER_POOL_ID',
      'COGNITO_CLIENT_ID',
      'COGNITO_IDENTITY_POOL_ID',
      'COGNITO_DOMAIN',
    ];

    const missingFields = requiredFields.filter(field => !config[field]);

    if (missingFields.length > 0) {
      throw new Error(`Missing required configuration fields: ${missingFields.join(', ')}`);
    }
  }

  getConfig(): AppConfig | null {
    return this.config;
  }

  isLoaded(): boolean {
    return this.config !== null;
  }
}

export const configService = new ConfigService();
